import React, { useEffect, useState } from "react";
import Breadcrumb from "../utlis/Breadcrumb";
import Header from "../Component/Header";
import Footer from "../Component/Footer";
import { getFaculty } from "../Service/Api";

const FALLBACK_IMAGE =
  "https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/CSSK/public/Images/dummy-test.png";

function Faculty() {
  const [facultyData, setFacultyData] = useState([]);
  const [activeTab, setActiveTab] = useState("management");
  const [loading, setLoading] = useState(true);

  // =====================================================
  // FETCH FACULTY
  // =====================================================
  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        setLoading(true);

        const response = await getFaculty();

        console.log("FACULTY API RESPONSE:", response);

        /*
         * API may return:
         * 1. Array
         * 2. { data: [] }
         * 3. { faculty: [] }
         * 4. { result: [] }
         * 5. { records: [] }
         */

        let data = [];

        if (Array.isArray(response)) {
          data = response;
        } else if (response && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response.faculty)) {
          data = response.faculty;
        } else if (response && Array.isArray(response.result)) {
          data = response.result;
        } else if (response && Array.isArray(response.records)) {
          data = response.records;
        }

        console.log("FINAL FACULTY DATA:", data);
        console.log("TOTAL FACULTY:", data.length);

        setFacultyData(data);
      } catch (error) {
        console.error("Faculty error:", error);
        setFacultyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFaculty();
  }, []);

  // =====================================================
  // CONVERT VALUE TO NORMALIZED STRING
  // =====================================================
  const normalizeValue = (value) => {
    if (value === undefined || value === null) {
      return "";
    }

    if (typeof value === "object") {
      if (value.name) {
        return value.name.toString().toLowerCase().trim();
      }

      if (value.title) {
        return value.title.toString().toLowerCase().trim();
      }

      if (value.value) {
        return value.value.toString().toLowerCase().trim();
      }

      return "";
    }

    return value.toString().toLowerCase().trim();
  };

  // =====================================================
  // GET ALL CATEGORY VALUES
  // =====================================================
  const getCategoryValues = (faculty) => {
    if (!faculty || typeof faculty !== "object") {
      return [];
    }

    const values = [
      faculty.category,
      faculty.facultyCategory,
      faculty.facultyType,
      faculty.employeeType,
      faculty.staffType,
      faculty.department,
      faculty.type,
      faculty.role,
      faculty.designation,
      faculty.designationName,
      faculty.post,
      faculty.employeeDesignation,
      faculty.employeeTypeName,
      faculty.staffCategory,
      faculty.staffCategoryName,

      // Additional possible API fields
      faculty.categoryName,
      faculty.category_name,
      faculty.faculty_category,
      faculty.faculty_type,
      faculty.employee_type,
      faculty.staff_type,
      faculty.staff_category,
      faculty.departmentName,
      faculty.department_name,
      faculty.jobTitle,
      faculty.job_title,
      faculty.position,
      faculty.positionName,
      faculty.position_name,
    ];

    return values
      .map((value) => normalizeValue(value))
      .filter((value) => value !== "");
  };

  // =====================================================
  // GET ALL TEXT FROM FACULTY OBJECT
  // =====================================================
  const getAllFacultyText = (faculty) => {
    if (!faculty || typeof faculty !== "object") {
      return "";
    }

    const values = getCategoryValues(faculty);

    return values.join(" ");
  };

  // =====================================================
  // CHECK MANAGEMENT
  // =====================================================
  const isManagement = (faculty) => {
    const values = getCategoryValues(faculty);
    const text = getAllFacultyText(faculty);

    /*
     * IMPORTANT:
     * Management is checked FIRST.
     *
     * This prevents Management records from accidentally
     * appearing under Assistant Teachers / Office & Others.
     */

    const managementKeywords = [
      "management",
      "management staff",
      "management member",
      "principal",
      "vice principal",
      "vice-principal",
      "head principal",
      "headmistress",
      "head mistress",
      "head master",
      "headmaster",
      "director",
      "administrator",
      "admin staff",
      "administration",
      "school administration",
      "coordinator",
      "academic coordinator",
      "school coordinator",
      "manager",
      "general manager",
      "assistant manager",
      "deputy manager",
      "supervisor",
      "chief",
    ];

    return (
      values.some((value) =>
        managementKeywords.some((keyword) => value.includes(keyword)),
      ) || managementKeywords.some((keyword) => text.includes(keyword))
    );
  };

  // =====================================================
  // CHECK NURSERY
  // =====================================================
  const isNurseryTeacher = (faculty) => {
    const values = getCategoryValues(faculty);

    return values.some(
      (value) =>
        value.includes("nursery") ||
        value.includes("pre nursery") ||
        value.includes("pre-nursery") ||
        value.includes("kindergarten") ||
        value.includes("pre primary") ||
        value.includes("pre-primary") ||
        value.includes("montessori") ||
        value.includes("kinder garden"),
    );
  };

  // =====================================================
  // CHECK ASSISTANT TEACHER
  // =====================================================
  const isAssistantTeacher = (faculty) => {
    const values = getCategoryValues(faculty);

    return values.some(
      (value) =>
        value.includes("assistant teacher") ||
        value.includes("assistant-teacher") ||
        value === "assistant" ||
        value.includes("teaching staff") ||
        value.includes("teacher") ||
        value.includes("faculty") ||
        value.includes("subject teacher") ||
        value.includes("tgt") ||
        value.includes("pgt") ||
        value.includes("prt") ||
        value.includes("pet"),
    );
  };

  // =====================================================
  // GET FACULTY CATEGORY
  // =====================================================
  const getFacultyCategory = (faculty) => {
    /*
     * VERY IMPORTANT:
     *
     * Priority:
     * 1. Management
     * 2. Nursery
     * 3. Assistant Teachers
     * 4. Office & Others
     *
     * Therefore one faculty member will only belong
     * to ONE tab.
     */

    if (isManagement(faculty)) {
      return "management";
    }

    if (isNurseryTeacher(faculty)) {
      return "nursery";
    }

    if (isAssistantTeacher(faculty)) {
      return "assistant";
    }

    return "office";
  };

  // =====================================================
  // MANAGEMENT DATA
  // =====================================================
  const managementData = facultyData.filter(
    (faculty) => getFacultyCategory(faculty) === "management",
  );

  // =====================================================
  // ASSISTANT TEACHER DATA
  // =====================================================
  const assistantTeacherData = facultyData.filter(
    (faculty) => getFacultyCategory(faculty) === "assistant",
  );

  // =====================================================
  // NURSERY DATA
  // =====================================================
  const nurseryTeacherData = facultyData.filter(
    (faculty) => getFacultyCategory(faculty) === "nursery",
  );

  // =====================================================
  // OFFICE & OTHERS DATA
  // =====================================================
  const officeOthersData = facultyData.filter(
    (faculty) => getFacultyCategory(faculty) === "office",
  );

  // =====================================================
  // DEBUG
  // =====================================================
  useEffect(() => {
    if (!loading) {
      console.log("======================================");
      console.log("TOTAL FACULTY:", facultyData.length);
      console.log("MANAGEMENT:", managementData.length);
      console.log("ASSISTANT TEACHERS:", assistantTeacherData.length);
      console.log("NURSERY TEACHERS:", nurseryTeacherData.length);
      console.log("OFFICE & OTHERS:", officeOthersData.length);
      console.log("======================================");

      console.log("MANAGEMENT DATA:", managementData);

      console.log("ASSISTANT TEACHER DATA:", assistantTeacherData);

      console.log("NURSERY TEACHER DATA:", nurseryTeacherData);

      console.log("OFFICE & OTHERS DATA:", officeOthersData);
    }
  }, [
    loading,
    facultyData,
    managementData.length,
    assistantTeacherData.length,
    nurseryTeacherData.length,
    officeOthersData.length,
  ]);

  // =====================================================
  // GET FACULTY NAME
  // =====================================================
  const getFacultyName = (faculty) => {
    return (
      faculty.name ||
      faculty.employeeName ||
      faculty.facultyName ||
      faculty.teacherName ||
      faculty.employee_name ||
      faculty.employee_name ||
      faculty.fullName ||
      faculty.full_name ||
      faculty.staffName ||
      faculty.staff_name ||
      faculty.title ||
      "Faculty"
    );
  };

  // =====================================================
  // GET DESIGNATION
  // =====================================================
  const getDesignation = (faculty) => {
    return (
      faculty.designation ||
      faculty.designationName ||
      faculty.employeeDesignation ||
      faculty.post ||
      faculty.role ||
      faculty.jobTitle ||
      faculty.job_title ||
      faculty.position ||
      faculty.positionName ||
      faculty.position_name ||
      ""
    );
  };

  // =====================================================
  // GET IMAGE
  // =====================================================
  const getFacultyImage = (faculty) => {
    return (
      faculty.image ||
      faculty.photo ||
      faculty.imageUrl ||
      faculty.profileImage ||
      faculty.employeeImage ||
      faculty.photoUrl ||
      faculty.employee_photo ||
      faculty.employeePhoto ||
      faculty.profile_image ||
      faculty.profile_image_url ||
      faculty.staffImage ||
      faculty.staff_image ||
      faculty.imagePath ||
      faculty.image_path ||
      ""
    );
  };

  // =====================================================
  // IMAGE URL
  // =====================================================
  const getImageUrl = (image) => {
    if (!image) {
      return FALLBACK_IMAGE;
    }

    if (typeof image !== "string") {
      return FALLBACK_IMAGE;
    }

    const cleanImage = image.trim();

    if (!cleanImage) {
      return FALLBACK_IMAGE;
    }

    // Complete URL
    if (cleanImage.startsWith("http://") || cleanImage.startsWith("https://")) {
      return cleanImage;
    }

    /*
     * Remove unwanted starting slashes so that
     * S3 path remains correct.
     */
    const cleanPath = cleanImage.replace(/^\/+/, "");

    return `https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/${cleanPath}`;
  };

  // =====================================================
  // FACULTY CARD
  // =====================================================
  const FacultyCard = ({ faculty }) => {
    const name = getFacultyName(faculty);

    const designation = getDesignation(faculty);

    const image = getFacultyImage(faculty);

    return (
      <div className="col-xl-3 col-lg-4 col-md-6 col-sm-6 col-12 mb-4">
        <div className="faculty-card">
          {/* IMAGE */}
          <div className="faculty-img">
            <img
              src={getImageUrl(image)}
              alt={name}
              className="img-fluid"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
          </div>

          {/* DETAILS */}
          <div className="faculty-info">
            <h5>{name}</h5>

            {designation && <p>{designation}</p>}
          </div>
        </div>
      </div>
    );
  };

  // =====================================================
  // GET SORT VALUE
  // =====================================================
  const getSortValue = (faculty) => {
    const value =
      faculty.order ??
      faculty.sortOrder ??
      faculty.sort_order ??
      faculty.sequence ??
      faculty.displayOrder ??
      faculty.display_order ??
      faculty.id ??
      0;

    const numberValue = Number(value);

    return Number.isNaN(numberValue) ? 0 : numberValue;
  };

  // =====================================================
  // RENDER FACULTY
  // =====================================================
  const renderFaculty = (data) => {
    if (loading) {
      return (
        <div className="col-12 text-center py-5">
          <p>Loading faculty...</p>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="col-12 text-center py-5">
          <p>No faculty found.</p>
        </div>
      );
    }

    return [...data]
      .sort((a, b) => getSortValue(b) - getSortValue(a))
      .map((faculty, index) => (
        <FacultyCard
          key={
            faculty._id ||
            faculty.id ||
            faculty.employeeId ||
            faculty.employee_id ||
            faculty.employeeCode ||
            faculty.staffId ||
            `${getFacultyName(faculty)}-${index}`
          }
          faculty={faculty}
        />
      ));
  };

  // =====================================================
  // TAB DATA
  // =====================================================
  const tabData = {
    management: managementData,
    assistant: assistantTeacherData,
    nursery: nurseryTeacherData,
    office: officeOthersData,
  };

  // =====================================================
  // CURRENT TAB DATA
  // =====================================================
  const currentTabData = tabData[activeTab] || [];

  // =====================================================
  // RETURN
  // =====================================================
  return (
    <>
      {/* =================================================
          HEADER
      ================================================= */}
      <Header />

      {/* =================================================
          BREADCRUMB
      ================================================= */}
      <div className="InnerSld">
        <Breadcrumb
          img="https://entab-s3-bucket1.s3.ap-south-1.amazonaws.com/CSSK/public/Images/page-title.jpg"
          title="Faculty"
          title2=""
          parentTitle="Academic"
          path="Faculty"
        />
      </div>

      {/* =================================================
          FACULTY SECTION
      ================================================= */}
      <div className="innerbody">
        <div className="container-fluid">
          <div className="contact-form-card manage">
            {/* PAGE TITLE */}
            <h2 className="about-title">Faculty</h2>

            <div className="about-content-card">
              <div className="row">
                <div className="col-md-12 maintable" id="faculty-content">
                  <div className="faculty-tabs-wrapper">
                    {/* =================================================
                        TABS
                    ================================================= */}
                    <ul
                      className="nav nav-tabs"
                      id="facultyTabs"
                      role="tablist"
                    >
                      {/* MANAGEMENT */}
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "management" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("management")}
                        >
                          Management
                        </button>
                      </li>

                      {/* ASSISTANT TEACHERS */}
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "assistant" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("assistant")}
                        >
                          Assistant Teachers
                        </button>
                      </li>

                      {/* NURSERY TEACHERS */}
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "nursery" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("nursery")}
                        >
                          Nursery Teachers
                        </button>
                      </li>

                      {/* OFFICE & OTHERS */}
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={`nav-link ${
                            activeTab === "office" ? "active" : ""
                          }`}
                          onClick={() => setActiveTab("office")}
                        >
                          Office and Others
                        </button>
                      </li>
                    </ul>

                    {/* =================================================
                        TAB CONTENT
                    ================================================= */}
                    <div className="tab-content pt-4">
                      <div
                        className="tab-pane fade show active"
                        role="tabpanel"
                      >
                        <div className="row">
                          {renderFaculty(currentTabData)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =================================================
          FOOTER
      ================================================= */}
      <Footer />
    </>
  );
}

export default Faculty;
